import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useAdminBillingDocumentController_createCreditNote } from '@api-client/generated/AdminBillingDocumentController/createCreditNote';
import { useAdminBillingDocumentController_createCreditNoteV3 } from '@api-client/generated/AdminBillingDocumentController/createCreditNoteV3';
import { useAdminBillingDocumentController_getCreditNoteCapabilities } from '@api-client/generated/AdminBillingDocumentController/getCreditNoteCapabilities';
import { Schemas } from '@api-client/generated/types';
import { DEFAULT_CURRENCY_CODE } from '@constants';
import { Amount } from '@entities';
import { InvoiceItemsFormList } from '@entities/billing/modals/InvoiceItemsFormList/InvoiceItemsFormList';
import { useCurrentCompany, useCurrentCompanyId } from '@hooks';
import { getApiErrorMessage } from '@utils';

import {
  buildCreditNoteRequest,
  CreditNoteChild,
  creditNoteTotal,
  CreditNoteMode,
  CustomLine,
  getCreditSourceHistory,
  hasCreditNoteContractV3,
  hasValidCustomLines,
  InvoiceItem,
  sumTotal,
} from './buildCreditNoteItems';

type FormValues = {
  reason: string;
  items?: CustomLine[];
};

type CreditNoteRequestIdentity = {
  fingerprint: string;
  idempotencyKey: string;
};

type ModalCreditNoteProps = {
  isOpen: boolean;
  parentId?: string;
  parentAmount?: number;
  parentCurrencyCode?: string;
  parentItems?: InvoiceItem[];
  creditNotes?: CreditNoteChild[];
  /** Sum already reversed by existing credit notes on this invoice. */
  alreadyCreditedTotal?: number;
  onClose: VoidFunction;
  onAfterCreate: VoidFunction;
};

export const ModalCreditNote = (props: ModalCreditNoteProps) => {
  const [form] = Form.useForm<FormValues>();
  const companyId = useCurrentCompanyId();
  const { data: company } = useCurrentCompany();

  const [mode, setMode] = useState<CreditNoteMode>('global');
  const [selectedJobs, setSelectedJobs] = useState<number[]>([]);
  const submitInFlightRef = useRef(false);
  const requestIdentityRef = useRef<CreditNoteRequestIdentity>();

  const {
    mutateAsync: createCreditNote,
    isPending: isLegacyCreditNotePending,
  } = useAdminBillingDocumentController_createCreditNote();
  const {
    mutateAsync: createCreditNoteV3,
    isPending: isV3CreditNotePending,
  } = useAdminBillingDocumentController_createCreditNoteV3();
  const {
    data: creditNoteCapabilities,
    isPending: isCreditNoteCapabilitiesPending,
  } =
    useAdminBillingDocumentController_getCreditNoteCapabilities({
      params: { companyId },
      config: {
        enabled: Boolean(props.isOpen && companyId),
        retry: false,
      },
    });

  const parentItems = useMemo(() => props.parentItems ?? [], [props.parentItems]);
  const currencyCode =
    props.parentCurrencyCode || DEFAULT_CURRENCY_CODE;
  const canCreateCreditNote = company && checkCompany(company);
  const supportsV3CreditNotes = hasCreditNoteContractV3(
    creditNoteCapabilities
  );
  const isCreateCreditNotePending =
    isLegacyCreditNotePending || isV3CreditNotePending;
  const sourceHistory = useMemo(
    () => getCreditSourceHistory(props.creditNotes, parentItems.length),
    [parentItems.length, props.creditNotes]
  );
  const hasExistingCreditNotes = Boolean(props.creditNotes?.length);
  const legacyAdditionalCreditBlocked =
    !supportsV3CreditNotes && hasExistingCreditNotes;
  const hasUnusedSourceItems = parentItems.some(
    (_, index) => !sourceHistory.usedIndexes.has(index)
  );
  const canUseGlobalMode = supportsV3CreditNotes
    ? parentItems.length > 0 &&
      !sourceHistory.hasUnknownSourceHistory &&
      sourceHistory.usedIndexes.size === 0
    : !legacyAdditionalCreditBlocked;
  const canUseJobsMode =
    supportsV3CreditNotes &&
    parentItems.length > 0 &&
    !sourceHistory.hasUnknownSourceHistory &&
    hasUnusedSourceItems;
  const canUseCustomMode = supportsV3CreditNotes;

  // Source modes (full invoice / jobs) need the issued line-item snapshot. It is
  // missing on legacy period invoices issued before item snapshots were stored,
  // so only custom credits are possible for them.
  const sourceModesUnavailable =
    supportsV3CreditNotes &&
    !sourceHistory.hasUnknownSourceHistory &&
    parentItems.length === 0;

  // Keep the selected mode on an option that is actually enabled — otherwise the
  // modal opens on the disabled default ('global') showing a €0.00 total with no
  // way to proceed except hunting for the one enabled radio.
  useEffect(() => {
    const isCurrentAvailable =
      (mode === 'global' && canUseGlobalMode) ||
      (mode === 'jobs' && canUseJobsMode) ||
      (mode === 'custom' && canUseCustomMode);
    if (isCurrentAvailable) return;

    if (canUseGlobalMode) setMode('global');
    else if (canUseJobsMode) setMode('jobs');
    else if (canUseCustomMode) setMode('custom');
  }, [mode, canUseGlobalMode, canUseJobsMode, canUseCustomMode]);

  // Live custom lines, so the credit total below reflects what's being typed.
  const customItems = Form.useWatch('items', form) as CustomLine[] | undefined;

  const creditTotal = useMemo(
    () => {
      const itemTotal = creditNoteTotal({
        mode,
        parentItems,
        selectedJobs,
        customLines: customItems ?? [],
      });

      return mode === 'global'
        ? itemTotal || props.parentAmount || 0
        : itemTotal;
    },
    [mode, selectedJobs, customItems, parentItems, props.parentAmount]
  );

  const hasValidCustomLine = hasValidCustomLines(customItems ?? []);

  // Mirror the backend guard: a credit note (of any mode) cannot reverse more
  // than what's left of the invoice after earlier credit notes.
  const invoiceTotal = useMemo(
    () => sumTotal(parentItems) || props.parentAmount || 0,
    [parentItems, props.parentAmount]
  );
  // Set for O(1) membership checks in the per-item render loop below.
  const selectedJobsSet = useMemo(() => new Set(selectedJobs), [selectedJobs]);
  const remaining = invoiceTotal - (props.alreadyCreditedTotal ?? 0);
  const hasValidInvoiceTotal =
    Number.isFinite(invoiceTotal) && invoiceTotal > 0;
  const hasValidCreditTotal =
    Number.isFinite(creditTotal) && creditTotal > 0;
  const exceedsRemaining =
    hasValidInvoiceTotal && creditTotal > remaining + 0.001;

  const canSubmit =
    Boolean(props.parentId) &&
    Boolean(canCreateCreditNote) &&
    !isCreditNoteCapabilitiesPending &&
    !isCreateCreditNotePending &&
    hasValidInvoiceTotal &&
    hasValidCreditTotal &&
    !exceedsRemaining &&
    ((mode === 'global' && canUseGlobalMode) ||
      (mode === 'jobs' &&
        canUseJobsMode &&
        selectedJobs.length > 0 &&
        selectedJobs.every((index) => !sourceHistory.usedIndexes.has(index))) ||
      (mode === 'custom' && canUseCustomMode && hasValidCustomLine));

  const resetState = () => {
    form.resetFields();
    setMode('global');
    setSelectedJobs([]);
    requestIdentityRef.current = undefined;
  };

  const handleClose = () => {
    resetState();
    props.onClose();
  };

  const handleSubmit = async (values: FormValues) => {
    const parentId = props.parentId;

    if (!parentId || !canSubmit || submitInFlightRef.current) {
      return;
    }

    submitInFlightRef.current = true;

    try {
      const requestBody: Schemas.AdminCreateCreditNoteDto = {
        parentId,
        issueDate: dayjs().format('YYYY-MM-DD'),
        reason: values.reason,
        ...buildCreditNoteRequest({
          mode,
          parentItemCount: parentItems.length,
          selectedJobs,
          customLines: values.items ?? [],
        }),
      };
      const request = {
        requestBody,
        parameter: {
          companyId,
        },
      };

      if (supportsV3CreditNotes) {
        const fingerprint = JSON.stringify({ companyId, requestBody });
        const currentIdentity = requestIdentityRef.current;
        const requestIdentity =
          currentIdentity?.fingerprint === fingerprint
            ? currentIdentity
            : {
                fingerprint,
                idempotencyKey: globalThis.crypto.randomUUID(),
              };
        requestIdentityRef.current = requestIdentity;

        await createCreditNoteV3({
          ...request,
          requestBody: {
            ...requestBody,
            idempotencyKey: requestIdentity.idempotencyKey,
          },
        });
      } else {
        await createCreditNote(request);
      }
    } catch (error) {
      message.error(getApiErrorMessage(error, 'Failed to create the credit note'));
      return;
    } finally {
      submitInFlightRef.current = false;
    }

    resetState();
    props.onClose();
    props.onAfterCreate();
  };

  const toggleJob = (index: number, checked: boolean) => {
    if (sourceHistory.usedIndexes.has(index)) return;

    setSelectedJobs((prev) =>
      checked ? [...prev, index] : prev.filter((i) => i !== index)
    );
  };

  return (
    <Modal
      open={props.isOpen}
      title="Create credit note"
      footer={null}
      onCancel={handleClose}
      destroyOnClose
      centered
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ reason: '', items: [{}] }}
        onFinish={handleSubmit}
      >
        <Form.Item label="Credit note type">
          <Radio.Group
            value={mode}
            onChange={(e) => setMode(e.target.value as CreditNoteMode)}
          >
            <Radio value="global" disabled={!canUseGlobalMode}>
              Full invoice
            </Radio>
            <Radio value="jobs" disabled={!canUseJobsMode}>
              Selected jobs
            </Radio>
            <Radio value="custom" disabled={!canUseCustomMode}>
              Custom line
            </Radio>
          </Radio.Group>
        </Form.Item>

        {!supportsV3CreditNotes && !legacyAdditionalCreditBlocked && (
          <Typography.Paragraph type="secondary">
            Selected jobs and custom credits require backend credit-note
            contract v3. Full-invoice credit remains available.
          </Typography.Paragraph>
        )}

        {legacyAdditionalCreditBlocked && (
          <Typography.Paragraph type="danger">
            Additional credit notes require backend credit-note contract v3.
          </Typography.Paragraph>
        )}

        {supportsV3CreditNotes && sourceHistory.hasUnknownSourceHistory && (
          <Typography.Paragraph type="danger">
            Previous credit-note source history cannot be verified. Source-based
            credit modes are unavailable; use a custom credit instead.
          </Typography.Paragraph>
        )}

        {sourceModesUnavailable && (
          <Typography.Paragraph type="secondary">
            This invoice has no stored line items, so full-invoice and per-job
            credits are unavailable. Create a custom credit instead.
          </Typography.Paragraph>
        )}

        {mode === 'jobs' && (
          <Form.Item label="Jobs to reverse">
            {parentItems.length ? (
              <Flex vertical gap={8}>
                {parentItems.map((item, index) => (
                  <Checkbox
                    key={index}
                    checked={selectedJobsSet.has(index)}
                    disabled={sourceHistory.usedIndexes.has(index)}
                    onChange={(e) => toggleJob(index, e.target.checked)}
                  >
                    <Flex justify="space-between" gap={16} style={{ width: '100%' }}>
                      <Typography.Text>{item.name}</Typography.Text>
                      {sourceHistory.usedIndexes.has(index) && (
                        <Typography.Text type="secondary">
                          Already credited
                        </Typography.Text>
                      )}
                      <Amount
                        currencyCode={currencyCode}
                        amount={item.totalPrice}
                        withoutType
                      />
                    </Flex>
                  </Checkbox>
                ))}
              </Flex>
            ) : (
              <Typography.Text type="secondary">
                This invoice has no line items to select.
              </Typography.Text>
            )}
          </Form.Item>
        )}

        {mode === 'custom' && (
          <InvoiceItemsFormList name="items" itemNoun="Line" />
        )}

        <Form.Item
          name="reason"
          label="Credit note reason"
          required
          rules={[{ required: true }]}
        >
          <Input.TextArea />
        </Form.Item>

        <Flex vertical align="flex-end" gap={4} style={{ marginBottom: 16 }}>
          <Flex align="center" gap={8}>
            <Typography.Text type="secondary">Credit total</Typography.Text>
            <Amount
              currencyCode={currencyCode}
              amount={creditTotal}
              withoutType
            />
          </Flex>
          {(props.alreadyCreditedTotal ?? 0) > 0 && (
            <Flex align="center" gap={8}>
              <Typography.Text type="secondary">
                Remaining to credit
              </Typography.Text>
              <Amount
                currencyCode={currencyCode}
                amount={remaining}
                withoutType
              />
            </Flex>
          )}
          {exceedsRemaining && (
            <Typography.Text type="danger">
              Credit total exceeds the remaining invoice amount.
            </Typography.Text>
          )}
        </Flex>

        {!canCreateCreditNote && (
          <Typography.Paragraph type="danger">
            To create a credit note, fill the address of the company on Company
            Info tab
          </Typography.Paragraph>
        )}
        <Flex gap={16} justify="flex-end" align="center">
          <Button onClick={handleClose} size="large">
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            disabled={!canSubmit}
            loading={isCreateCreditNotePending}
          >
            Create
          </Button>
        </Flex>
      </Form>
    </Modal>
  );
};

const checkCompany = (company: Schemas.AdminCompanyResponseDto) =>
  !!(
    company?.name &&
    company?.details?.address?.houseNumber &&
    company?.details?.address?.street &&
    company?.details?.address?.postalCode &&
    company?.details?.address?.city
  );
